import { loginAsPromptAtom } from 'app/atoms/app';
import { http } from 'app/lib/axios';
import { recordError } from 'app/lib/monitoring';
import { StorageKey, storage } from 'app/lib/storage';
import type { User } from 'app/types/user';
import { useSetAtom } from 'jotai';
import { type Profile } from 'app/types/profile';
import { updateSessionState } from 'app/lib/axios/session-state';

type AskToLoginAs = (userName: string, currentUserPicture?: string, loginAsUserPicture?: string) => Promise<boolean>;

async function performLoginAsPrompt(askToLoginAs: AskToLoginAs, clearLoginAsPrompt: () => void) {
  try {
    const [storedLoginAsUserId, storedLoginAsEmail] = await Promise.all([
      storage.readString(StorageKey.LoginAsUserId),
      storage.readString(StorageKey.LoginAsEmail),
    ]);

    if (storedLoginAsUserId || storedLoginAsEmail) {
      return undefined;
    }

    const user = (await http.get<User>('/users')).data;
    if (!user.loginAsId) {
      return undefined;
    }

    const [currentUserProfile, userProfile] = await Promise.all([
      http.get<Profile>(`/profiles/${user.id}`).then((r) => r.data),
      http
        .get<Profile>(`/profiles/${user.loginAsId}`, {
          headers: { 'LPC-Login-As': user.loginAsId },
        })
        .then((r) => r.data),
    ]);

    const shouldLoginAs = await askToLoginAs(userProfile.firstName, currentUserProfile.picture, userProfile.picture);

    clearLoginAsPrompt();

    if (!shouldLoginAs) {
      return undefined;
    }

    await storage.clear(StorageKey.LoginAsEmail);
    updateSessionState({ loginAsEmail: null });

    const loginAsUser = (
      await http.get<User>('/users', {
        headers: { 'LPC-Login-As': user.loginAsId },
      })
    ).data;

    if (loginAsUser.id !== user.loginAsId) {
      recordError(new Error(`Expected login-as user ${user.loginAsId}, got ${loginAsUser.id}`));
      return undefined;
    }

    await storage.saveString(StorageKey.CurrentUserId, loginAsUser.id);
    await storage.saveString(StorageKey.LoginAsUserId, user.loginAsId);
    updateSessionState({ currentUserId: loginAsUser.id, loginAsUserId: user.loginAsId });

    return loginAsUser;
  } catch (error) {
    clearLoginAsPrompt();
    recordError(error);
    return undefined;
  }
}

export function useLoginAsPrompt() {
  const setLoginAsPrompt = useSetAtom(loginAsPromptAtom);

  const askToLoginAs = (userName: string, currentUserPicture?: string, loginAsUserPicture?: string) => {
    return new Promise<boolean>((resolve) => {
      setLoginAsPrompt({ resolve, userName, currentUserPicture, loginAsUserPicture });
    });
  };

  return () => performLoginAsPrompt(askToLoginAs, () => setLoginAsPrompt(null));
}
